import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AsyncPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { RouterModule } from '@angular/router';

import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { MatOption } from '@angular/material/autocomplete';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';

import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { BehaviorSubject, debounceTime, Subject, takeUntil } from 'rxjs';

import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { SORTING_OPTIONS } from 'src/app/shared/used-components/hotels-booking-container/utils';
import { OccasionGeniusEvent } from 'src/app/core/types/models/occasion-genius-event.model';
import { OccasionGeniusResponse } from 'src/app/core/types/models/occasion-genius-response.model';
import { ETripCityInfoType } from 'src/app/core/enums/trip-planner-view.enum';
import { LocationListModule } from 'src/app/shared/location-list/location-list.module';
import { HotelCardMobileComponent } from '../hotel-card-mobile/hotel-card-mobile.component';
import { ViatorCardMobileComponent } from '../viator-card-mobile/viator-card-mobile.component';

import { OccasionGeniusCardMobileComponent } from '../occasion-genius-card-mobile/occasion-genius-card-mobile.component';
import { VoiceGuideCardMobileComponent } from '../voice-guide-card-mobile/voice-guide-card-mobile.component';
import { HotelListMobileComponent } from '../hotel-map-mobile/hotel-map-mobile.component';
import { HotelFiltersMobileComponent } from '../hotel-filters-mobile/hotel-filters-mobile.component';

import { PackageService } from 'src/app/core/services/package.service';
import { ScrollToIdService } from 'src/app/core/services/scroll-to-id.service';
import { AudioPlayerService } from 'src/app/core/services/audio-player.service';
import { OccasionGeniusService } from 'src/app/core/services/occasion-genius.service';
import { HotelsBookingProviderService } from 'src/app/shared/used-components/hotels-booking-container/hotels-booking-provider.service';

import { HotelSearchComponent } from '../hotel-search/hotel-search.component';
import { IViatorEvent } from 'src/app/shared/used-components/viator/viator.component';
import { NoItemsFoundComponent } from 'src/app/shared/components/no-items-found/no-items-found.component';
import { OccasionGeniusFiltersMobileComponent } from '../occasion-genius-filters-mobile/occasion-genius-filters-mobile.component';

import moment from 'moment';

type PageState =
  | 'default-list'
  | 'hotels-map'
  | 'hotels-filters'
  | 'hotels-search'
  | 'occasion-genius-filters';

@Component({
  selector: 'app-show-all-mobile',
  templateUrl: './show-all-mobile.component.html',
  styleUrls: ['show-all-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    LocationListModule,
    TranslateModule,
    MatIcon,
    HotelCardMobileComponent,
    ViatorCardMobileComponent,
    OccasionGeniusCardMobileComponent,
    VoiceGuideCardMobileComponent,
    HotelListMobileComponent,
    HotelFiltersMobileComponent,
    MatOption,
    MatSelect,
    OccasionGeniusFiltersMobileComponent,
    NgxSkeletonLoaderModule,
    NoItemsFoundComponent,
    CdkVirtualScrollViewport,
    CdkVirtualForOf,
    CdkFixedSizeVirtualScroll,
    AsyncPipe,
    DatePipe,
    TitleCasePipe,
  ],
  providers: [
    HotelsBookingProviderService,
    OccasionGeniusService,
    AudioPlayerService,
  ],
})
export class ShowAllMobileComponent implements OnInit, OnDestroy {
  state: PageState = 'default-list';
  clonedItems: any[] = [];
  hotelFilters: Record<string, any> = {
    facilities: [],
    stars: [],
    score: null,
    accomondation: [],
    priceRange: {
      min: null,
      max: null,
    },
    sorting: null,
    refundable: false,
    breakfastIncluded: false,
  };
  occasionGeniusFilters: Record<string, any> = {
    from: '',
    to: '',
    score: '',
    search: '',
    categories: [],
    events: [],
  };
  occasionCategoriesOptions: { label: string; value: string }[] = [
    { label: '1-25', value: '0' },
    { label: '26-99', value: '1' },
    { label: '100-999', value: '2' },
    { label: '1,000-9,999', value: '3' },
    { label: '10,000-99,000', value: '4' },
    { label: '100,000-999,999', value: '5' },
    { label: '1,000,000+', value: '6' },
  ];
  protected readonly sortingOptions = SORTING_OPTIONS;
  protected readonly eSvgIcons = ESvgIcons;
  protected readonly ETripCityInfoType = ETripCityInfoType;
  private onSearch$ = new BehaviorSubject('');
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA)
    public data: {
      items: any[];
      pageType: ETripCityInfoType;
      cityId: string | number;
      countryName: string;
      cityName: string;
      isFetching: boolean;
      packages$?: BehaviorSubject<any[]>;
      currentDestinationIndex?: number;
      groupViator?: { type: string; events: IViatorEvent[] }[];
      groupViatorIndex?: number;
      occasionGeniusResponse?: OccasionGeniusResponse;
    },
    private matDialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private packageService: PackageService,
    private scrollToIdService: ScrollToIdService,
    private occasionGeniusService: OccasionGeniusService,
    private hotelsBookingProvider: HotelsBookingProviderService,
  ) {
    this.setClonedItems(data.items);
  }

  ngOnInit() {
    this.data.packages$.pipe(takeUntil(this.destroy$)).subscribe((packages) => {
      if (packages.length) {
        if (this.data.pageType === ETripCityInfoType.OCCASION_GENIUS) {
          this.fetchOccasionGeniusWithFilters();
        }

        if (this.data.pageType === ETripCityInfoType.BOOKING) {
          this.fetchHotelsWithFilters(this.hotelFilters);
        }
      }
    });

    this.onSearch$
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((value) => {
        if (
          [ETripCityInfoType.BOOKING, ETripCityInfoType.VIATOR].includes(
            this.data.pageType,
          )
        ) {
          this.clonedItems = this.data.items.filter((i) =>
            i.name.toLowerCase().trim().includes(value.toLowerCase().trim()),
          );
        }
        if ([ETripCityInfoType.VOICE_GUIDE].includes(this.data.pageType)) {
          this.clonedItems = this.data.items.filter((i) =>
            i.title.toLowerCase().trim().includes(value.toLowerCase().trim()),
          );
        }
        if ([ETripCityInfoType.OCCASION_GENIUS].includes(this.data.pageType)) {
          this.clonedItems =
            this.data.occasionGeniusResponse.eventsSearchedAndSorted.filter(
              (e) =>
                e.name
                  .toLowerCase()
                  .trim()
                  .includes(value.toLowerCase().trim()),
            );
        }
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(value: string): void {
    this.onSearch$.next(value);
  }

  setState(state: PageState): void {
    this.state = state;
  }

  applyHotelFiltersHandler({ requestBody, filters }): void {
    this.hotelFilters = filters;
    this.setState('default-list');
    this.fetchHotelsWithFilters(requestBody);
  }

  applyOccasionGeniusFiltersHandler({ requestBody, filters }): void {
    this.occasionGeniusFilters = filters;
    this.setState('default-list');
    this.fetchOccasionGeniusWithFilters(requestBody);
  }

  setClonedItems(items: any[]): void {
    this.data.isFetching = false;
    this.clonedItems = Array.isArray(items) ? [...items] : [];
    this.cdr.markForCheck();
  }

  fetchHotelsWithFilters(filters?: any) {
    this.data.isFetching = true;
    this.hotelsBookingProvider
      .getHotelsCollection(
        this.data.packages$.value[this.data.currentDestinationIndex].id as any,
        {
          start: `${this.data.packages$.value[this.data.currentDestinationIndex].dates.start}`,
          end: this.data.packages$.value[this.data.currentDestinationIndex]
            .dates.end,
        },
        this.cleanObject(filters),
      )
      .subscribe({
        next: (hotels) => {
          this.setClonedItems(hotels);
          this.data.isFetching = false;
        },
      });
  }

  fetchOccasionGeniusWithFilters(filters?: any): void {
    this.data.isFetching = true;
    this.occasionGeniusService
      .getOccasionGeniusAll(this.data.cityId as any, {
        from: filters?.from
          ? moment(filters.from).unix()
          : this.data.packages$.value[this.data.currentDestinationIndex].dates
              .start,
        to: filters?.to
          ? moment(filters.to).unix()
          : this.data.packages$.value[this.data.currentDestinationIndex].dates
              .end,
        offset: 0,
      })
      .subscribe({
        next: (response) => {
          const occasionGeniusResponse = new OccasionGeniusResponse(response);
          this.data.occasionGeniusResponse.updateEvents(
            occasionGeniusResponse.events,
          );
          this.data.occasionGeniusResponse.updateScoreFilters(
            filters?.score ? [filters.score] : [],
          );
          this.clonedItems = occasionGeniusResponse.eventsSearchedAndSorted;
          this.data.isFetching = false;
          this.cdr.detectChanges();
        },
      });
  }

  fetchViator(): void {
    this.data.isFetching = true;
    this.packageService
      .getViatorCityEvents(
        this.data.packages$.value[this.data.currentDestinationIndex],
      )
      .then((res) => {
        this.data.groupViator = [];

        Object.keys(res).forEach((key) => {
          this.data.groupViator.push({
            type: key,
            events: res[key],
          });
        });

        this.setViatorGroupIndex(0);

        this.data.isFetching = false;
        this.cdr.markForCheck();
      });
  }

  getCardEvent(event: any): OccasionGeniusEvent[] {
    return event?.events?.length ? event.events : [event];
  }

  setViatorGroupIndex(index: any) {
    this.data.groupViatorIndex = index;
    this.clonedItems = this.data.groupViator[this.data.groupViatorIndex].events;
    this.cdr.markForCheck();
  }

  setOccasionGroupType(value: string) {
    this.data.occasionGeniusResponse.updateOneCategoryFilterState(value);
    this.clonedItems = this.data.occasionGeniusResponse.eventsSearchedAndSorted;
    this.cdr.markForCheck();
  }

  prevCity(): void {
    this.data.currentDestinationIndex -= 1;

    if (this.data.pageType === ETripCityInfoType.BOOKING) {
      this.fetchHotelsWithFilters(this.hotelFilters);
    } else if (this.data.pageType === ETripCityInfoType.OCCASION_GENIUS) {
      this.fetchOccasionGeniusWithFilters(this.occasionCategoriesOptions);
    } else if (this.data.pageType === ETripCityInfoType.VIATOR) {
      this.fetchViator();
    }
  }

  nextCity(): void {
    this.data.currentDestinationIndex += 1;

    if (this.data.pageType === ETripCityInfoType.BOOKING) {
      this.fetchHotelsWithFilters(this.hotelFilters);
    } else if (this.data.pageType === ETripCityInfoType.OCCASION_GENIUS) {
      this.fetchOccasionGeniusWithFilters(this.occasionCategoriesOptions);
    } else if (this.data.pageType === ETripCityInfoType.VIATOR) {
      this.fetchViator();
    }
  }

  searchHotel(): void {
    const hotels = [...this.clonedItems].splice(0, 100);
    this.matDialog
      .open(HotelSearchComponent, {
        width: '768px',
        maxWidth: '98vw',
        maxHeight: '100vh',
        panelClass: 'mobile-container',
        data: hotels,
      })
      .afterClosed()
      .subscribe((hotelId) => {
        if (!hotelId) return;
        const index = hotels.findIndex((hotel: any) => hotel.id === hotelId);
        this.scrollToIdService.scrollToWithContainer(
          index,
          'hotel-el-',
          '.hotel-list-filters',
          16,
        );
      });
  }

  private cleanObject(formData: any) {
    const cleanedObj: Record<string, any> = {};

    Object.keys(formData).forEach((key) => {
      const val = formData[key];
      if (val !== undefined && val !== null) {
        if (val instanceof Array && val.length > 0) {
          cleanedObj[key] = val;
        }
      }
    });
    if (formData.priceRange) {
      if (formData.priceRange.min) {
        cleanedObj.priceRange = {
          min: formData.priceRange.min,
        };
      }
      if (formData.priceRange.max) {
        cleanedObj.priceRange.max = formData.priceRange.max;
      }
    }
    if (formData.sort) cleanedObj.sort = formData.sort;
    if (formData.score) cleanedObj.score = formData.score;
    if (formData.refundable) cleanedObj.refundable = formData.refundable;
    if (formData.breakfastIncluded)
      cleanedObj.breakfastIncluded = formData.breakfastIncluded;
    return cleanedObj;
  }
}
